/**
 * Generation Summary Service
 *
 * Aggregates MatchMetadata from generated dinners to produce
 * human-readable summary lines explaining how the plan was built.
 */

import { GeneratedDinner } from './weekGeneratorService';
import i18n from 'i18next';

export type SummaryLineType = 'taste' | 'inventory' | 'deals' | 'variety' | 'coverage';

export interface SummaryLine {
  type: SummaryLineType;
  text: string;
  icon: string; // Feather icon name
}

export interface GenerationSummary {
  lines: SummaryLine[];
}

/**
 * Build a human-readable generation summary from an array of generated dinners.
 * Aggregates match metadata to produce concise summary lines.
 */
export function buildGenerationSummary(dinners: GeneratedDinner[]): GenerationSummary {
  const lines: SummaryLine[] = [];

  if (dinners.length === 0) return { lines };

  // === Taste Profile ===
  const dinnersWithTasteBonus = dinners.filter(
    d => d.matchMetadata?.tasteProfileBonus && d.matchMetadata.tasteProfileBonus > 0
  );
  if (dinnersWithTasteBonus.length > 0) {
    const count = dinnersWithTasteBonus.length;
    lines.push({
      type: 'taste',
      text: i18n.t('generationSummary.tasteMeals', { count }),
      icon: 'zap',
    });
  }

  // === Inventory Usage ===
  const dinnersWithInventory = dinners.filter(
    d => d.matchMetadata?.inventoryMatchPercent && d.matchMetadata.inventoryMatchPercent > 0
  );
  if (dinnersWithInventory.length > 0) {
    const count = dinnersWithInventory.length;
    lines.push({
      type: 'inventory',
      text: i18n.t('generationSummary.inventoryMeals', { count }),
      icon: 'package',
    });
  }

  // === Deal Integration ===
  const totalDeals = dinners.reduce(
    (sum, d) => sum + (d.matchMetadata?.dealCount || 0), 0
  );
  if (totalDeals > 0) {
    lines.push({
      type: 'deals',
      text: i18n.t('generationSummary.dealsIncluded', { count: totalDeals }),
      icon: 'tag',
    });
  }

  // === Ingredient Coverage ===
  const dinnersWithCoverage = dinners.filter(
    d => d.matchMetadata?.ingredientCoverage
  );
  if (dinnersWithCoverage.length > 0) {
    const avgCoverage = Math.round(
      dinnersWithCoverage.reduce(
        (sum, d) => sum + d.matchMetadata!.ingredientCoverage!.coveragePercent, 0
      ) / dinnersWithCoverage.length
    );
    const totalToBuy = dinnersWithCoverage.reduce(
      (sum, d) => sum + d.matchMetadata!.ingredientCoverage!.toBuyCount, 0
    );
    lines.push({
      type: 'coverage',
      text: i18n.t('generationSummary.coverageSummary', {
        avgPercent: avgCoverage,
        toBuyCount: totalToBuy,
        mealCount: dinnersWithCoverage.length,
      }),
      icon: 'pie-chart',
    });
  }

  // === Cuisine Variety ===
  const allCuisines = new Set<string>();
  for (const dinner of dinners) {
    for (const cuisine of dinner.recipe.cuisines) {
      allCuisines.add(cuisine.toLowerCase());
    }
  }
  if (allCuisines.size > 1) {
    lines.push({
      type: 'variety',
      text: i18n.t('generationSummary.cuisineVariety', { count: allCuisines.size }),
      icon: 'globe',
    });
  }

  return { lines };
}
